import { Box, Grid } from '@mui/material';
import React, { FC, useMemo, useRef, useState } from 'react';

import 'swiper/css';
import 'swiper/css/autoplay';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { SliderItemProvider, SliderProvider } from '@/data/context/content';

type Props = {
	slides?: JSX.Element[];
	children?: JSX.Element[];
	id?: string;
	withoutmargin?: boolean;
};

export const CarouselSlider: FC<Props> = ({ id, slides, withoutmargin = false, children }) => {
	const _slides = useMemo(() => {
		if (slides) return slides;
		const cleanChildren = React.Children.toArray(children).filter((child) =>
			React.isValidElement(child)
		);
		return cleanChildren;
	}, [slides, children]);

	const [activeSlide, setActiveSlide] = useState(0);
	const sliderRef = useRef<any>(null);
	const [mouseMoved, setMouseMoved] = useState(false);

	if (_slides.length === 1) {
		return (
			<SliderProvider value={{ mouseMoved, activeSlide }}>
				<Box
					id={id}
					width="100vw"
					minWidth="100%"
					maxWidth="100%"
					marginBottom={withoutmargin ? '' : { xs: '20px', sm: '40px' }}
				>
					{_slides}
				</Box>
			</SliderProvider>
		);
	}

	return (
		<Box
			id={id}
			width="100vw"
			minWidth="100%"
			maxWidth="100%"
			position="relative"
			marginBottom={withoutmargin ? '' : { xs: '20px', sm: '40px' }}
			sx={{ overflowX: 'hidden' }}
		>
			<SliderProvider value={{ mouseMoved, activeSlide }}>
				<Swiper
					modules={[Autoplay]}
					loop={true}
					autoplay={{
						delay: 8000,
						disableOnInteraction: false,
					}}
					slidesPerView={1}
					onSwiper={
						((swiper: any) => {
							sliderRef.current = swiper;
						}) as any
					}
					onSlideChange={(swiper: any) => {
						setActiveSlide(swiper.realIndex);
					}}
					onSlideChangeTransitionStart={() => {
						setMouseMoved(true);
					}}
					onSlideChangeTransitionEnd={() => {
						setMouseMoved(false);
					}}
				>
					{_slides.map((slide, index) => (
						<SwiperSlide key={`id_carousel_${index}`}>
							<SliderItemProvider value={{ index }}>
								<Grid>{slide}</Grid>
							</SliderItemProvider>
						</SwiperSlide>
					))}
				</Swiper>
			</SliderProvider>
			{_slides.length > 1 ? (
				<Box
					width="100vw"
					minWidth="100vw"
					maxWidth="100vw"
					position={'absolute'}
					justifyContent={'center'}
					display={'inline-flex'}
					bottom={'20px'}
					zIndex={'20'}
				>
					<Box
						sx={{
							display: 'flex',
							direction: 'row',
							gap: '0px !important',
							borderRadius: '10px',
							backgroundColor: '#FFFFFF80',
						}}
					>
						{Array.from({ length: _slides.length }).map((_, index) => (
							<Box
								key={`id_carousel_dot_${index}`}
								sx={{
									backgroundColor: 'white',
									width: {
										xs: `calc(150px/${_slides.length})`,
										md: `calc(300px/${_slides.length})`,
									},
									height: '6px',
									margin: '0px !important',
									padding: '0px !important',
									opacity: activeSlide === index ? '1 !important' : '0.5',
									borderTopLeftRadius: index === 0 ? '10px' : '0px',
									borderBottomLeftRadius: index === 0 ? '10px' : '0px',
									borderTopRightRadius: index === _slides.length - 1 ? '10px' : '0px',
									borderBottomRightRadius: index === _slides.length - 1 ? '10px' : '0px',
									cursor: 'pointer',
								}}
								onClick={() => {
									sliderRef.current?.slideToLoop(index);
								}}
							/>
						))}
					</Box>
				</Box>
			) : null}
		</Box>
	);
};
